/**
 * Member Authentication Context
 * Manages auth state, user session, and protected route access
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { User, AuthTokens, MemberProfile, DashboardSummary } from '../types/member';
import memberApi, { 
  getAuthToken, 
  getUserIdFromToken,
  setAuthTokens, 
  clearAuthTokens, 
  isAuthenticated as checkIsAuthenticated,
  setOnAuthFailure,
} from '../services/memberApi';

// ============================================================================
// CONTEXT TYPE
// ============================================================================

interface MemberAuthContextType {
  // State
  user: User | null;
  profile: MemberProfile | null;
  dashboard: DashboardSummary | null;
  agentStatus: { agent_status: string | null; agent_code: string | null } | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isVerifying: boolean;
  
  // Auth actions
  login: (email: string, password: string) => Promise<void>;
  socialLogin: (provider: 'google' | 'apple' | 'facebook', token: string) => Promise<void>;
  signup: (data: {
    first_name: string;
    surname: string;
    email: string;
    password?: string;
    data_processing_consent: boolean;
    marketing_consent: boolean;
  }) => Promise<{ requiresVerification: boolean }>;
  socialSignup: (provider: 'google' | 'apple' | 'facebook', token: string, consent: {
    data_processing_consent: boolean;
    marketing_consent: boolean;
  }) => Promise<{ requiresVerification: boolean }>;
  logout: () => Promise<void>;
  
  // Verification
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  
  // Password reset
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (token: string, newPassword: string) => Promise<void>;
  
  // Profile
  updateProfile: (data: Partial<MemberProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshDashboard: () => Promise<void>;
  refreshAgentStatus: () => Promise<void>;
  
  // Error handling
  error: string | null;
  clearError: () => void;
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const MemberAuthContext = createContext<MemberAuthContextType | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export function MemberAuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [agentStatus, setAgentStatus] = useState<{ agent_status: string | null; agent_code: string | null } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Shared init logic — also used when re-syncing from another tab
  // Shared so that login/socialLogin populate `user` exactly the way the
  // mount-time init does. login() previously set profile/dashboard but never
  // `user`, so `user?.is_super_admin` stayed undefined for the whole session and
  // every admin page rendered "Access Denied" until a hard reload.
  const applyUserFromProfile = useCallback((profileData: any) => {
    if (!profileData) return;
    setUser({
      id: profileData.user_id || getUserIdFromToken() || '',
      email: '',
      email_verified: true,
      first_name: '',
      surname: '',
      status: 'active',
      created_at: '',
      is_super_admin: profileData.is_super_admin,
    });
  }, []);

  const initAuth = useCallback(async () => {
    if (checkIsAuthenticated()) {
      try {
        const [profileData, dashboardData, agentStatusData] = await Promise.all([
          memberApi.profile.getProfile(),
          memberApi.profile.getDashboard(),
          memberApi.agent.getStatus().catch(() => ({ agent_status: null, agent_code: null })),
        ]);

        if (profileData) {
          setProfile(profileData);
          setUser({
            id: profileData.user_id || getUserIdFromToken() || '',
            email: '',
            email_verified: true,
            first_name: '',
            surname: '',
            status: 'active',
            created_at: '',
            is_super_admin: profileData.is_super_admin,
          });
        }

        if (dashboardData) {
          setDashboard(dashboardData);
        }

        setAgentStatus(agentStatusData);
      } catch {
        clearAuthTokens();
        setUser(null);
        setProfile(null);
        setDashboard(null);
        setAgentStatus(null);
      }
    } else {
      setUser(null);
      setProfile(null);
      setDashboard(null);
      setAgentStatus(null);
    }
    setIsLoading(false);
  }, []);

  // Register global auth-failure redirect handler
  useEffect(() => {
    setOnAuthFailure(() => {
      clearAuthTokens();
      setUser(null);
      setProfile(null);
      setDashboard(null);
      // Avoid redirect loop if already on login page
      if (!location.pathname.startsWith('/member/login')) {
        navigate(`/member/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`);
      }
    });
  }, [navigate, location.pathname, location.search]);

  // Check auth status on mount
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  // Cross-tab session sync via BroadcastChannel + storage events
  useEffect(() => {
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel('marple_auth');
      channel.onmessage = (event) => {
        if (event.data?.type === 'LOGIN' || event.data?.type === 'LOGOUT') {
          initAuth();
        }
      };
    } catch {
      // BroadcastChannel not supported — fall back to storage events only
    }

    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'marple_member_token') {
        initAuth();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      channel?.close();
      window.removeEventListener('storage', handleStorage);
    };
  }, [initAuth]);

  // ============================================================================
  // AUTH ACTIONS
  // ============================================================================

  const _broadcastAuth = (type: 'LOGIN' | 'LOGOUT') => {
    try {
      const ch = new BroadcastChannel('marple_auth');
      ch.postMessage({ type });
      ch.close();
    } catch {
      // ignore — not all browsers support BroadcastChannel
    }
  };

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const tokens = await memberApi.auth.login({ email, password });
      setAuthTokens(tokens);

      // Fetch user data
      const [profileData, dashboardData, agentStatusData] = await Promise.all([
        memberApi.profile.getProfile(),
        memberApi.profile.getDashboard(),
        memberApi.agent.getStatus().catch(() => ({ agent_status: null, agent_code: null })),
      ]);

      setProfile(profileData);
      applyUserFromProfile(profileData);
      setDashboard(dashboardData);
      setAgentStatus(agentStatusData);
      _broadcastAuth('LOGIN');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [applyUserFromProfile]);

  const socialLogin = useCallback(async (provider: 'google' | 'apple' | 'facebook', token: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const tokens = await memberApi.auth.socialLogin({ provider, token });
      setAuthTokens(tokens);

      const [profileData, dashboardData, agentStatusData] = await Promise.all([
        memberApi.profile.getProfile(),
        memberApi.profile.getDashboard(),
        memberApi.agent.getStatus().catch(() => ({ agent_status: null, agent_code: null })),
      ]);

      setProfile(profileData);
      applyUserFromProfile(profileData);
      setDashboard(dashboardData);
      setAgentStatus(agentStatusData);
      _broadcastAuth('LOGIN');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Social login failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [applyUserFromProfile]);

  const signup = useCallback(async (data: {
    first_name: string;
    surname: string;
    email: string;
    password?: string;
    data_processing_consent: boolean;
    marketing_consent: boolean;
  }) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await memberApi.auth.signup(data);
      return { requiresVerification: result.requires_verification };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Signup failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const socialSignup = useCallback(async (
    provider: 'google' | 'apple' | 'facebook',
    token: string,
    consent: { data_processing_consent: boolean; marketing_consent: boolean }
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await memberApi.auth.socialSignup({
        provider,
        token,
        ...consent,
      });
      return { requiresVerification: result.requires_verification };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Social signup failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await memberApi.auth.logout();
    } catch {
      // Ignore logout errors
    } finally {
      clearAuthTokens();
      setUser(null);
      setProfile(null);
      setDashboard(null);
      _broadcastAuth('LOGOUT');
    }
  }, []);

  // ============================================================================
  // VERIFICATION
  // ============================================================================

  const verifyEmail = useCallback(async (token: string) => {
    setIsVerifying(true);
    setError(null);

    try {
      await memberApi.auth.verifyEmail(token);
      
      // If we have a session, update user
      if (checkIsAuthenticated()) {
        setUser(prev => prev ? { ...prev, email_verified: true } : null);
        // Also refresh the dashboard. ProtectedRoute gates on
        // dashboard.needs_verification, so leaving it stale bounced a
        // just-verified user between /member/dashboard and /member/verify-email
        // with no way out short of clearing localStorage.
        try {
          const dashboardData = await memberApi.profile.getDashboard();
          if (dashboardData) setDashboard(dashboardData);
        } catch {
          // Non-critical: verification itself succeeded.
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verification failed';
      setError(message);
      throw err;
    } finally {
      setIsVerifying(false);
    }
  }, []);

  const resendVerification = useCallback(async (email: string) => {
    setError(null);

    try {
      await memberApi.auth.resendVerification(email);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resend verification';
      setError(message);
      throw err;
    }
  }, []);

  // ============================================================================
  // PASSWORD RESET
  // ============================================================================

  const requestPasswordReset = useCallback(async (email: string) => {
    setError(null);

    try {
      await memberApi.auth.requestPasswordReset(email);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to request password reset';
      setError(message);
      throw err;
    }
  }, []);

  const confirmPasswordReset = useCallback(async (token: string, newPassword: string) => {
    setError(null);

    try {
      await memberApi.auth.confirmPasswordReset(token, newPassword);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset password';
      setError(message);
      throw err;
    }
  }, []);

  // ============================================================================
  // PROFILE
  // ============================================================================

  const updateProfile = useCallback(async (data: Partial<MemberProfile>) => {
    setError(null);

    try {
      const updated = await memberApi.profile.updateProfile(data);
      setProfile(updated);
      
      // Refresh dashboard as completion status may have changed
      const dashboardData = await memberApi.profile.getDashboard();
      setDashboard(dashboardData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update profile';
      setError(message);
      throw err;
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const profileData = await memberApi.profile.getProfile();
      setProfile(profileData);
    } catch (err) {
      console.error('Failed to refresh profile:', err);
    }
  }, []);

  const refreshDashboard = useCallback(async () => {
    try {
      const dashboardData = await memberApi.profile.getDashboard();
      setDashboard(dashboardData);
    } catch (err) {
      console.error('Failed to refresh dashboard:', err);
    }
  }, []);

  const refreshAgentStatus = useCallback(async () => {
    try {
      const data = await memberApi.agent.getStatus();
      setAgentStatus(data);
    } catch (err) {
      console.error('Failed to refresh agent status:', err);
    }
  }, []);

  // ============================================================================
  // ERROR HANDLING
  // ============================================================================

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value: MemberAuthContextType = {
    user,
    profile,
    dashboard,
    agentStatus,
    isAuthenticated: checkIsAuthenticated(),
    isLoading,
    isVerifying,
    login,
    socialLogin,
    signup,
    socialSignup,
    logout,
    verifyEmail,
    resendVerification,
    requestPasswordReset,
    confirmPasswordReset,
    updateProfile,
    refreshProfile,
    refreshDashboard,
    refreshAgentStatus,
    error,
    clearError,
  };

  return (
    <MemberAuthContext.Provider value={value}>
      {children}
    </MemberAuthContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useMemberAuth() {
  const context = useContext(MemberAuthContext);
  if (context === undefined) {
    throw new Error('useMemberAuth must be used within a MemberAuthProvider');
  }
  return context;
}

export default MemberAuthContext;
